/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useCallback, useEffect } from "react";
import Layout from "../components/Layout";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import {
  fetchProductDetails,
  getPrintSettings,
  saveBarcode,
  savePrintSettings,
} from "../services/api";
import { Product } from "../types/product";
import DetailProduct from "../components/DetailProduct";
import PrintSettings from "../components/PrintSetting";
import PrintPreview from "../components/PrintPreview";
import ModalGenerateBarcode from "../components/ModalGenerateBarcode";
import { PrintSettingsStateTypes } from "../types/print_settings";

const getInitialPrintSettings = (): PrintSettingsStateTypes => ({
  showName: true,
  showPrice: true,
  showCode: true,
  showBarcode: true,
  showDate: true,
  showDisplay: false,
  showPriceDisplay: true,
  showCodeDisplay: true,
  showNameDisplay: true,
  showDateDisplay: true,
  barcodeWidth: 2,
  barcodeHeight: 60,
  pageWidth: 15,
  pageHeight: 5,
  fontSize: 16,
  fontPrice: 16,
  marginTop: 3,
  marginBottom: 0,
  marginLeft: 10,
  marginRight: 10,
  barcodeType: "CODE128",
  printPerLine: 2,
  displayQuantity: 1,
  printQuantity: 1,
  printLine: "2",
  printGrouping: "item",
});

const ImportItem: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [listProducts, setListProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [printSettings, setPrintSettings] = useState<PrintSettingsStateTypes>(
    getInitialPrintSettings(),
  );
  const [modalGenerate, setModalGenerate] = useState<boolean>(false);
  const [product, setProduct] = useState<Product | null>(null);
  // Fetch saved print settings on mount
  const fetchPrintSettings = useCallback(async () => {
    try {
      const data = await getPrintSettings();
      if (data?.data?.printSettings) {
        const parsed = JSON.parse(data.data.printSettings);
        setPrintSettings(parsed?.payloadPrintSetting);
      }
    } catch (err) {
      console.error("Failed fetch print settings", err);
    }
  }, []);

  useEffect(() => {
    fetchPrintSettings();
  }, []);

  // Print settings handlers
  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const { name, type, value, checked } = e.target;
      const newPrintSettings = {
        ...printSettings,
        [name]: type === "checkbox" ? checked : value,
      };
      setPrintSettings(newPrintSettings);
      await savePrintSettings(newPrintSettings);
    } catch (err: any) {
      toast.error(err.message || "Failed to save print settings");
    }
  };

  const handleQuantityChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = e.target;
    if (Number(value) > 0 || value === "") {
      const newPrintSettings = { ...printSettings, [name]: value };
      setPrintSettings(newPrintSettings);
      await savePrintSettings(newPrintSettings);
    }
  };

  // File handlers
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      if (
        droppedFile.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        droppedFile.type === "application/vnd.ms-excel" ||
        droppedFile.name.endsWith(".xlsx") ||
        droppedFile.name.endsWith(".xls")
      ) {
        setFile(droppedFile);
      } else {
        toast.error("Invalid file format. Please upload an Excel file.");
      }
    }
  };

  // Excel processing: parse file → search API per row → match product
  const processExcelFile = async (excelFile: File) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows: any[] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        // Skip header row: [Product Code, Product Name, Print Qty]
        const dataRows = rows.slice(1).filter((r) => r[0]);

        if (dataRows.length === 0) {
          toast.error("No data found in Excel file");
          setIsUploading(false);
          return;
        }

        setIsLoading(true);
        const foundProducts: Product[] = [];
        let notFoundCount = 0;

        for (const row of dataRows) {
          const productCode = String(row[0]).trim();
          const productName = String(row[1] || "").trim();
          const printQty = Number(row[2]) || 1;

          if (!productCode) continue;

          try {
            const res = await fetchProductDetails(productCode);
            if (res?.data?.items) {
              const items = Array.isArray(res.data.items)
                ? res.data.items
                : [res.data.items];

              let matched: Product | undefined;

              // 1. Exact match by itemNumber or itemBarcode
              matched = items.find(
                (item) =>
                  item.itemNumber === productCode ||
                  item.itemBarcode === productCode,
              );

              // 2. If multiple items with same code, match by Product Name from Excel
              if (items.length > 1 && productName) {
                const nameMatch = items.find(
                  (item) =>
                    item.itemName.toLowerCase() === productName.toLowerCase(),
                );
                if (nameMatch) matched = nameMatch;
              }

              // 3. Fallback to first result
              if (!matched && items.length > 0) {
                matched = items[0];
              }

              if (matched) {
                foundProducts.push({ ...matched, printQty });
              } else {
                notFoundCount++;
              }
            } else {
              notFoundCount++;
            }
          } catch {
            notFoundCount++;
            console.error(`Failed to fetch product: ${productCode}`);
          }
        }

        // Merge with existing list, avoid duplicates by itemNumber
        setListProducts((prev) => {
          const combined = [...prev, ...foundProducts];
          const unique = combined.filter(
            (item, idx, self) =>
              idx === self.findIndex((t) => t.itemNumber === item.itemNumber),
          );
          return unique;
        });

        toast.success(`Berhasil import ${foundProducts.length} item`);
        if (notFoundCount > 0) {
          toast.warning(`${notFoundCount} item tidak ditemukan`);
        }
      } catch (error) {
        console.error("Error processing Excel:", error);
        toast.error("Gagal memproses file Excel");
      } finally {
        setIsLoading(false);
        setIsUploading(false);
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };
    reader.readAsArrayBuffer(excelFile);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }
    setIsUploading(true);
    processExcelFile(file);
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "/Template_export.xlsx";
    link.download = "Template_export.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Item list handlers
  const handleRemoveItem = (index: string) => {
    setListProducts((prev) =>
      prev.filter((_item, idx) => idx !== Number(index)),
    );
  };

  const onGenerateBarcode = async (
    itemNumber?: string,
    barcodeItem?: string,
  ) => {
    try {
      setIsLoading(true);
      const payload = { keyword: itemNumber, barcodeItem };
      const data = (await saveBarcode(payload)) as any;
      if (data?.data?.upcNo) {
        toast.success("Barcode generated successfully");
        setListProducts((prev) =>
          prev.map((item) =>
            item.itemNumber === itemNumber
              ? { ...item, itemBarcode: data.data.upcNo }
              : item,
          ),
        );
      } else {
        toast.error("Failed to generate barcode");
      }
    } catch {
      toast.error("Failed to generate barcode");
    } finally {
      setIsLoading(false);
    }
  };

  const listWithBarcode = listProducts.filter(
    (item) => item.itemBarcode && item.itemBarcode.length > 0,
  );

  return (
    <Layout breadcrumbTitle="Import Item">
      <div className="w-full bg-white p-6 h-fit rounded-lg shadow-md">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">
            Import Item from Excel
          </h1>
          <button
            onClick={handleDownloadTemplate}
            className="flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors duration-200"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            Download Template
          </button>
        </div>

        {/* Drag & Drop Zone */}
        <div
          className={`border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center transition-colors duration-300 ${
            isDragOver
              ? "border-blue-500 bg-blue-50"
              : "border-gray-300 bg-gray-50 hover:bg-gray-100"
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="mb-4">
            <svg
              className={`w-16 h-16 ${
                isDragOver ? "text-blue-500" : "text-gray-400"
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
          </div>
          <p className="mb-2 text-lg text-gray-700 font-medium">
            {file ? file.name : "Drag & Drop your Excel file here"}
          </p>
          <p className="text-gray-500 text-sm mb-6">
            or click to browse from your computer
          </p>
          <input
            type="file"
            accept=".xlsx, .xls"
            onChange={handleFileChange}
            className="hidden"
            ref={fileInputRef}
            id="file-upload"
          />
          <label
            htmlFor="file-upload"
            className="cursor-pointer bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold py-2 px-4 rounded shadow-sm transition-all duration-200"
          >
            Choose File
          </label>
          <p className="mt-4 text-xs text-gray-400">
            Supported formats: .xlsx, .xls
          </p>
        </div>

        {/* Upload Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleUpload}
            disabled={!file || isUploading}
            className={`px-8 py-3 rounded-lg font-semibold text-white shadow-md transition-all duration-200 flex items-center ${
              !file || isUploading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 hover:shadow-lg transform hover:-translate-y-0.5"
            }`}
          >
            {isUploading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Processing...
              </>
            ) : (
              "Upload Excel"
            )}
          </button>
        </div>

        {/* Loading Indicator */}
        {isLoading && (
          <div className="flex justify-center items-center mt-4">
            <span className="loader-clock"></span>
          </div>
        )}

        {/* Imported Items List */}
        {listProducts.length > 0 && (
          <div className="mt-8 border p-4 rounded shadow-md">
            <DetailProduct
              onGenerateBarcode={(product) => {
                setModalGenerate(true);
                setProduct(product);
              }}
              isLoading={isLoading}
              handleRemoveItem={handleRemoveItem}
              lists={listProducts}
              onUpdateProduct={(index, field, value) => {
                setListProducts((prev) =>
                  prev.map((p, i) =>
                    i === index ? { ...p, [field]: value } : p,
                  ),
                );
              }}
            />

            {listWithBarcode.length > 0 && (
              <>
                <PrintSettings
                  printSettings={printSettings}
                  fetchPrintSettings={fetchPrintSettings}
                />
                <PrintPreview
                  settings={printSettings}
                  handleChange={handleChange}
                  handleQuantityChange={handleQuantityChange}
                  lists={listProducts}
                />
              </>
            )}
          </div>
        )}
      </div>

      <ModalGenerateBarcode
        isOpen={modalGenerate}
        onClose={() => {
          setModalGenerate(false);
          setProduct(null);
        }}
        onGenerateBarcode={onGenerateBarcode}
        product={product}
      />
    </Layout>
  );
};

export default ImportItem;
